// Cambios sin guardar: diferencias campo a campo entre lo guardado y lo que hay en pantalla,
// y un registro global para que una sola barra junte los cambios de todos los formularios abiertos.

export type CampoCambio<T> = {
  id: keyof T & string;
  // Nombre del campo como lo ve la persona ("Nombre", "Hora de envio").
  t: string;
  // Como se muestra el valor en "Antes: ..." y en la lista de cambios.
  fmt?: (v: T[keyof T]) => string;
  // Para comparar: por defecto los textos se comparan sin espacios a los lados.
  igual?: (a: T[keyof T], b: T[keyof T]) => boolean;
};

export type Cambio = { id: string; t: string; antes: string; ahora: string };

const VACIO = '(vacío)';

export function textoValor(v: unknown): string {
  if (v === null || v === undefined || v === '') return VACIO;
  if (typeof v === 'boolean') return v ? 'Sí' : 'No';
  if (Array.isArray(v)) return v.length ? v.map(textoValor).join(', ') : VACIO;
  if (typeof v === 'object') return JSON.stringify(v);
  const s = String(v).replace(/\s+/g, ' ').trim();
  if (!s) return VACIO;
  return s.length > 60 ? `${s.slice(0, 58)}…` : s;
}

export function mismoValor(a: unknown, b: unknown): boolean {
  if (typeof a === 'string' && typeof b === 'string') return a.trim() === b.trim();
  // null, undefined y '' cuentan como "vacio".
  const vacio = (x: unknown) => x === null || x === undefined || x === '';
  if (vacio(a) && vacio(b)) return true;
  if (typeof a === 'object' || typeof b === 'object') return JSON.stringify(a) === JSON.stringify(b);
  return a === b;
}

export function diferencias<T>(inicial: T, actual: T, campos: CampoCambio<T>[]): Cambio[] {
  const out: Cambio[] = [];
  for (const c of campos) {
    const a = inicial[c.id];
    const b = actual[c.id];
    if ((c.igual ?? mismoValor)(a, b)) continue;
    const f = c.fmt ?? textoValor;
    out.push({ id: c.id, t: c.t, antes: f(a), ahora: f(b) });
  }
  return out;
}

export const textoCambios = (n: number) => (n === 1 ? '1 cambio' : `${n} cambios`);

/* ---------- registro global ---------- */

export type Registro = {
  id: string;
  titulo: string;
  cambios: Cambio[];
  // Motivo por el que no se puede guardar (campo invalido), o null.
  invalido: string | null;
  guardar: () => Promise<boolean>;
  descartar: () => void;
};

// Pregunta "Antes de ir a ...": la barra la muestra y la persona elige.
export type Aviso = { destino: string; ir: () => void; quedar: () => void };

type Estado = { registros: Registro[]; barras: string[]; aviso: Aviso | null };

let estado: Estado = { registros: [], barras: [], aviso: null };
const subs = new Set<() => void>();
const emitir = (e: Partial<Estado>) => {
  estado = { ...estado, ...e };
  subs.forEach((s) => s());
};

export const estadoCambios = () => estado;
export function suscribirCambios(cb: () => void) {
  subs.add(cb);
  return () => {
    subs.delete(cb);
  };
}

export function registrar(r: Registro) {
  const i = estado.registros.findIndex((x) => x.id === r.id);
  const registros = [...estado.registros];
  if (i >= 0) registros[i] = r;
  else registros.push(r);
  emitir({ registros, aviso: totalDe(registros) ? estado.aviso : null });
}

export function quitar(id: string) {
  if (!estado.registros.some((x) => x.id === id)) return;
  const registros = estado.registros.filter((x) => x.id !== id);
  emitir({ registros, aviso: totalDe(registros) ? estado.aviso : null });
}

// Varias barras pueden montarse (una por formulario): solo la primera se dibuja.
export function montarBarra(id: string) {
  if (!estado.barras.includes(id)) emitir({ barras: [...estado.barras, id] });
}
export function desmontarBarra(id: string) {
  if (estado.barras.includes(id)) emitir({ barras: estado.barras.filter((x) => x !== id) });
}

export const totalDe = (rs: Registro[]) => rs.reduce((a, r) => a + r.cambios.length, 0);
export const hayCambios = () => totalDe(estado.registros) > 0;

export function pedirAviso(a: Aviso | null) {
  emitir({ aviso: a });
}

// Primer motivo que impide guardar entre los formularios con cambios.
export const invalidoDe = (rs: Registro[]) => rs.find((r) => r.cambios.length && r.invalido)?.invalido ?? null;

// Guarda en orden los formularios con cambios; se detiene en el primero que falle.
export async function guardarTodo(rs = estado.registros) {
  for (const r of rs.filter((x) => x.cambios.length)) if (!(await r.guardar())) return false;
  return true;
}

export function descartarTodo(rs = estado.registros) {
  rs.filter((x) => x.cambios.length).forEach((r) => r.descartar());
}

// Solo para las pruebas.
export function _reiniciar() {
  estado = { registros: [], barras: [], aviso: null };
}
